import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { fetchTeamFileLink } from '@/api/v4/team-files.api';
import { QueryKey } from '@/config/query-client';
import type {
  DraftFile,
  File,
  FileLinkDownloadHandler,
  FileModeType,
} from '@/features/action-panel/files/file.interface';
import { FilesTabContent } from '@/features/action-panel/files/files-tab-content';
import type { UploadFileInputProps } from '@/features/action-panel/files/automatic-upload-file-button';
import { teamIdState } from '@/features/teams/team.state';
import { downloadLinkAutomatically } from '@/helpers/download-link-automatically';
import { modalState } from '@/state/modal.state';
import { notify } from '@/ui/snackbar/notify';

import { useShareTeamFile } from './use-share-team-file.mutation';
import { useTeamFileScanningStatus } from './use-team-file-scanning-status.query';
import { useTeamFiles } from './use-team-files.query';
import { useUploadTeamFile } from './use-upload-team-file.mutation';
import { useAtomValue, useSetAtom } from 'jotai';

export const TeamFilesTab = () => {
  const { t } = useTranslation('default');
  const teamId = useAtomValue(teamIdState);
  const queryClient = useQueryClient();

  const { uploadTeamFile } = useUploadTeamFile();
  const { shareTeamFile } = useShareTeamFile();

  const { data } = useTeamFiles();
  const files = data ?? [];

  const setModal = useSetAtom(modalState);

  if (!teamId) return null;

  const openDeleteModal = async (id: string, name: string) => {
    setModal({
      state: 'deleteTeamFile',
      file: { id, name },
      teamId,
    });
  };

  const handleSubmitFiles: UploadFileInputProps['onSubmit'] = async file => {
    if (!teamId) return;

    setDraftFiles(file);

    try {
      await uploadTeamFile(file);
    } catch (error) {
      if (error instanceof Error) {
        notify({ message: error.message });
      }
    }
  };

  const handleFileLinkDownload: FileLinkDownloadHandler = async (
    file: File,
    mode: FileModeType,
  ) => {
    if (!teamId) return;

    const link = await fetchTeamFileLink({ teamId, fileId: file.id }, { mode });

    await downloadLinkAutomatically(link, file.name);
  };

  const setDraftFiles = (uploadingFiles: globalThis.File[]) => {
    queryClient.setQueryData(
      [QueryKey.TeamFiles, teamId],
      (files: Array<File | DraftFile> | undefined) => {
        if (!files) return;

        const draftFiles: DraftFile[] = uploadingFiles.map(file => ({
          id: file.name,
          name: file.name,
          createdDate: new Date().toISOString(),
          scanningStatus: null,
          creator: null,
          public: true,
          lastEditedDate: null,
        }));
        return [...draftFiles, ...files];
      },
    );
  };

  return (
    <FilesTabContent
      files={files}
      onFileLinkDownload={handleFileLinkDownload}
      emptyListLabel={t('actionPanel.files.empty', { context: 'team' })}
      onSubmit={handleSubmitFiles}
      onClickDelete={openDeleteModal}
      onShareFile={async payload => {
        await shareTeamFile(payload);
      }}
      useFileScanningStatus={useTeamFileScanningStatus}
    />
  );
};
